import { useState } from 'react';
import { X } from 'lucide-react';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Modal, Textarea } from '@techsofit/ui';

/**
 * Voiding a payment now requires a reason, so the gym has a record of why it was reversed.
 * Drop this in with the payment context; `onConfirm` gets the trimmed reason and performs the void.
 */
export function VoidPaymentModal({
  description,
  onClose,
  onConfirm,
}: {
  description?: string;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function go() {
    if (!reason.trim()) {
      setError('Enter a reason for voiding this payment.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
      // Parent closes on success.
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not void the payment. Please try again.');
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={busy ? () => undefined : onClose}>
      <div className="flex w-full flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-ink">Void this payment?</h2>
          <button type="button" aria-label="Close" onClick={onClose} disabled={busy} className="text-muted hover:text-ink disabled:opacity-50">
            <X className="size-5" aria-hidden />
          </button>
        </div>

        {description && <p className="text-sm text-muted">{description}</p>}

        <Textarea
          label="Reason for voiding"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          autoFocus
          placeholder="e.g. recorded twice by mistake, wrong amount, refunded in cash"
          hint="Kept on record so you know why this payment was reversed."
        />

        {error && (
          <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="danger" onClick={go} loading={busy}>Void payment</Button>
        </div>
      </div>
    </Modal>
  );
}
